import * as React from "react"
import { Check, Plus, ChevronDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import { Label } from "./label"

interface WritableSelectProps {
  value: string
  onValueChange: (value: string) => void
  options: Array<{ id: number | string; name: string }>
  onCreateNew?: (name: string) => Promise<void>
  placeholder?: string
  label?: string
  id?: string
  className?: string
  disabled?: boolean
  error?: boolean
}

export function WritableSelect({
  value,
  onValueChange,
  options,
  onCreateNew,
  placeholder = "Type to search...",
  label,
  id,
  className,
  disabled = false,
  error = false
}: WritableSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(value)
  const [isCreating, setIsCreating] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  // Sync internal inputValue with external value prop
  React.useEffect(() => {
    setInputValue(value)
  }, [value])

  // Filter options based on input value
  const filteredOptions = React.useMemo(() => {
    if (!inputValue.trim()) return options
    return options.filter(option =>
      option.name.toLowerCase().includes(inputValue.toLowerCase())
    )
  }, [options, inputValue])

  // Check if current input value exists in options
  const exactMatch = options.find(option => 
    option.name.toLowerCase() === inputValue.toLowerCase()
  )

  // Check if we should show the create button
  const shouldShowCreate = onCreateNew != null && inputValue.trim() && !exactMatch && !isCreating

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onValueChange(newValue)
    setIsOpen(true)
  }

  // Handle clear selection
  const handleClear = () => {
    setInputValue('')
    onValueChange('')
    setIsOpen(false)
  }

  // Handle option selection
  const handleOptionSelect = (option: { id: number | string; name: string }) => {
    setInputValue(option.name)
    onValueChange(option.name)
    setIsOpen(false)
    inputRef.current?.blur()
  }

  // Handle create new option
  const handleCreateNew = async () => {
    if (!onCreateNew || !inputValue.trim() || isCreating) return

    setIsCreating(true)
    try {
      await onCreateNew(inputValue.trim())
      setInputValue(inputValue.trim())
      onValueChange(inputValue.trim())
      setIsOpen(false)
    } catch (error) {
      console.error('Error creating new option:', error)
    } finally {
      setIsCreating(false)
    }
  }

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true)
  }

  // Handle input blur
  const handleBlur = (e: React.FocusEvent) => {
    // Don't close if clicking on dropdown
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
      return
    }
    setIsOpen(false)
  }

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && shouldShowCreate) {
      e.preventDefault()
      handleCreateNew()
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={cn("relative", className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pr-16",
            inputValue && "pr-20",
            error && "border-red-500 focus:border-red-500"
          )}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-1">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown 
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </div>
      
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-auto"
        >
          {filteredOptions.length > 0 ? (
            <div className="p-1">
              {filteredOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleOptionSelect(option)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground flex items-center justify-between",
                    exactMatch?.id === option.id && "bg-accent text-accent-foreground"
                  )}
                >
                  <span>{option.name}</span>
                  {exactMatch?.id === option.id && (
                    <Check className="h-4 w-4" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="p-3 text-sm text-muted-foreground text-center">
              No options found
            </div>
          )}
          
          {shouldShowCreate && (
            <div className="border-t p-1">
              <Button
                onClick={handleCreateNew}
                disabled={isCreating}
                variant="ghost"
                size="sm"
                className="w-full justify-start"
              >
                <Plus className="h-4 w-4 mr-2" />
                {isCreating ? 'Creating...' : `Create "${inputValue.trim()}"`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
